import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  Col,
  Row,
  Badge,
  Pagination,
  Modal,
  Table,
  Tag,
  message,
  Button,
} from "antd";
import CircularProgressCard from "./CircularProgressCard";
import { CopyOutlined } from "@ant-design/icons";
import useAuth from "../hooks/useAuth";
import moment from "moment";

const colors = {
  "REQ Approved": "green",
  Hold: "#00B4D2",
  Inactive: "red",
};

const statusColors = {
  Selected: "green",
  L1: "yellow",
  L2: "blue",
  Rejected: "red",
  HR: "skyblue",
  Processing: "purple",
};

const URL = process.env.REACT_APP_API_URL;

const JobDashboard = ({ jobs, selectedStatus, searchQuery }) => {
  const [candidateCounts, setCandidateCounts] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const { token, auth } = useAuth();

  const pageSize = 16;

  useEffect(() => {
    const fetchJobsByStatus = async () => {
      try {
        let response;
        if (selectedStatus === "All") {
          response = await axios.get(`${URL}/api/viewjobs`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } else {
          response = await axios.get(`${URL}/api/jobs`, {
            params: { status: selectedStatus },
            headers: { Authorization: `Bearer ${token}` },
          });
        }

        let jobsData = response.data;
        jobsData.sort((a, b) => new Date(b.postedAt) - new Date(a.postedAt));

        if (auth.role === "HiringManager") {
          jobsData = jobsData.filter((job) => auth.vertical.includes(job.vertical));
        }

        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          jobsData = jobsData.filter((item) => {
            const position = item.position?.toLowerCase() || "";
            const location = item.location?.toLowerCase() || "";
            const postedBy = item.postedBy?.toLowerCase() || "";
            const primarySkills = Array.isArray(item.primarySkills)
              ? item.primarySkills.join(", ").toLowerCase()
              : item.primarySkills?.toLowerCase() || "";
            return (
              position.includes(query) ||
              location.includes(query) ||
              postedBy.includes(query) ||
              primarySkills.includes(query)
            );
          });
        }

        setFilteredJobs(jobsData);
      } catch (error) {
        console.error("Error fetching jobs by status:", error);
      }
    };

    fetchJobsByStatus();
  }, [selectedStatus, token, auth.role, auth.vertical, searchQuery]);

  useEffect(() => {
    const fetchCandidateCounts = async () => {
      try {
        const response = await axios.get(`${URL}/api/positions`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const countsObject = response.data.reduce((acc, job) => {
          acc[job.positionId] = job.registeredCandidates;
          return acc;
        }, {});

        setCandidateCounts(countsObject);
      } catch (error) {
        console.error("Error fetching candidate counts:", error);
      }
    };

    fetchCandidateCounts();
  }, [token]);

  const copyJobLink = (jobId) => {
    const jobURL = `${window.location.origin}/register-job/${jobId}`;
    navigator.clipboard?.writeText(jobURL)
      .then(() => message.success("Link copied to clipboard!"))
      .catch((err) => console.error("Failed to copy job URL: ", err));
  };

const showApplicants = async (job) => {
  try {
    const response = await axios.get(`${URL}/api/applicants/position/${job.positionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const sortedApplicants = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    setApplicants(sortedApplicants);
    setSelectedJob(job); // store full job object
    setIsModalVisible(true);
  } catch (error) {
    console.error("Error fetching applicants:", error);
  }
};


  const renderResumeLink = (row) => {
    if (!row.resume) return "Resume not available";
    const downloadLink = row.resume.includes("google.com") ? row.resume : `${URL}${row.resume}`;
    return <a href={downloadLink} target="_blank" rel="noopener noreferrer">{row.firstName} CV</a>;
  };

  const handleShortlist = async (id) => {
    try {
      const now = new Date();
      let updatedCandidate;

      setApplicants((prev) =>
        prev.map((candidate) => {
          if (candidate._id !== id) return candidate;
          const updatedTatHistory = [...candidate.tatHistory];
          const opsIndex = updatedTatHistory.findIndex((t) => t.tatType === "OPS TAT" && !t.endDate);
          if (opsIndex !== -1) updatedTatHistory[opsIndex] = { ...updatedTatHistory[opsIndex], endDate: now.toISOString(), tatStatus: "CV Shortlisted" };
          updatedTatHistory.push({ tatType: "HR TAT", startDate: now.toISOString(), tatStatus: "Screening to be done" });
          updatedCandidate = { ...candidate, status: "CV Shortlisted", tatHistory: updatedTatHistory };
          return updatedCandidate;
        })
      );

      const response = await axios.put(`${URL}/api/candidate/${id}`, updatedCandidate, {
        headers: { Authorization: `Bearer ${token}` },
      });

      response.data.status === "SUCCESS"
        ? message.success("CV Shortlisted and HR TAT added successfully!")
        : message.error("Failed to update candidate");
    } catch (error) {
      console.error("Error in handleShortlist:", error);
      message.error("An error occurred while processing the request.");
    }
  };

  const handleReject = async (id) => {
    try {
      const now = new Date();
      let updatedCandidate;

      setApplicants((prev) =>
        prev.map((candidate) => {
          if (candidate._id !== id) return candidate;
          const updatedTatHistory = [...candidate.tatHistory];
          const opsIndex = updatedTatHistory.findIndex((t) => t.tatType === "OPS TAT" && !t.endDate);
          if (opsIndex !== -1) updatedTatHistory[opsIndex] = { ...updatedTatHistory[opsIndex], endDate: now.toISOString(), tatStatus: "CV Rejected" };
          updatedCandidate = { ...candidate, status: "CV Rejected", tatHistory: updatedTatHistory };
          return updatedCandidate;
        })
      );

      const response = await axios.put(`${URL}/api/candidate/${id}`, updatedCandidate, {
        headers: { Authorization: `Bearer ${token}` },
      });

      response.data.status === "SUCCESS"
        ? message.success("Candidate rejected successfully!")
        : message.error("Failed to reject candidate");
    } catch (error) {
      console.error("Error in handleReject:", error);
      message.error("An error occurred while rejecting the candidate.");
    }
  };

  const columns = [
    { title: "Full Name", dataIndex: "fullName", key: "fullName" },
    { title: "Resume/CV", key: "resume", render: (text, record) => renderResumeLink(record) },
    { title: "Notice Period", dataIndex: "noticePeriod", key: "noticePeriod" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag style={{ width: "100%", textAlign: "center" }} color={statusColors[status] || "gray"}>
          {status}
        </Tag>
      ),
    },
  ];

  if (auth.role === "HiringManager") {
    columns.push({
      title: "Action on CV",
      key: "edit",
      render: (text, record) => record.status === "CV Sourced" && (
        <span className="actions-cv-buttons">
          <Button type="link" onClick={() => handleShortlist(record._id)}>Shortlist</Button>
          <Button type="link" onClick={() => handleReject(record._id)}>Reject</Button>
        </span>
      ),
    });
  }

  const startIndex = (currentPage - 1) * pageSize;
  const currentJobs = filteredJobs.slice(startIndex, startIndex + pageSize);

  return (
    <div>
      <Row gutter={[16, 16]}>
        {currentJobs.map((job) => {
          const daysRemaining = moment(job.fullfilledBy).diff(moment(), "days");
          return (
            <Col key={job._id} xs={20} sm={12} md={8} lg={6}>
            <Badge
  count={candidateCounts[job.positionId] || 0}
  style={{ backgroundColor: "#1DAB4D" }}
  showZero
>

                <div className="card-container">
                  <div className="card-flip">
                    <Card
                      className="card-front"
                      bordered={false}
                      style={{ margin: "0 auto", backgroundColor: "#FFFF", height: "180px", width: "230px", textAlign: "left", cursor: "pointer" }}
                      onClick={() => showApplicants(job)}
                    >
                      <div className="card-title" style={{ cursor: "pointer" }}>
                        <span style={{ fontWeight: "bold", textDecoration: "underline", color: "#00B4D2" }}>{job.position}</span>
                      </div>
                      <p>
                        <strong>Location:</strong> {job.jobLocation}
                      </p>
                      <p>
                        <strong>HR:</strong> {job.postedBy}
                      </p>
                      <p>
                        <strong>Dept:</strong> {job.vertical}
                      </p>
                      <p>
                        <strong>Status:</strong>{" "}
                        <span
                          style={{
                            color: colors[job.status],
                            fontWeight: "bold",
                          }}
                        >
                          {job.status}
                        </span>
                      </p>
                      {job.status === "REQ Approved" && job.fullfilledBy !== null && (
                        <p>
                          {/* <strong>Closes in:</strong> {daysRemaining} days */}
                        </p>
                      )}
                    </Card>
                    <Card
                      className="card-back"
                     onClick={() => showApplicants(job)}
                      bordered={false}
                      style={{ backgroundColor: "#FFFF", display: "inline-block", position: "relative" }}
                    >
                      <CircularProgressCard job={job} onboardedCount={job.onboardedCount || 0}/>
                    </Card>
                  </div>
                </div>
              </Badge>
            </Col>
          );
        })}
      </Row>
      <Pagination
        current={currentPage}
        pageSize={pageSize}
        total={filteredJobs.length}
        onChange={(page) => setCurrentPage(page)}
        style={{ textAlign: "right", marginTop: "20px", paddingTop: "10px", background: "#fff", maxWidth: "100%", height: "50px", boxShadow: "0px 1px 2px rgb(38, 39, 130)" }}
      />
      <Modal
        title={null}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        <Row justify="space-between" align="middle">
          <Col><h2>Applicants for {selectedJob?.position}</h2></Col>
          <Col>
            <Button
              icon={<CopyOutlined />}
              onClick={() => copyJobLink(selectedJob)}
              style={{ backgroundColor: "#00B4D2", color: "white", height: "auto", margin: "20px 10px" }}
            >
              Copy Job Registration Link
            </Button>
          </Col>
        </Row>
        <Table columns={columns} dataSource={applicants} rowKey="_id" style={{ textTransform: "capitalize" }} pagination={{ pageSize: 8 }} />
      </Modal>
    </div>
  );
};

export default JobDashboard;